import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Fade,
  Grow,
  useTheme,
  alpha,
  Divider,
  Chip,
} from '@mui/material'
import {
  BlockOutlined,
  ContactSupport,
  InfoOutlined,
  WarningAmber,
  EmailOutlined,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

interface ProviderBlockedPageProps {
  providerName?: string
  blockReason?: string | null
}

/**
 * Professional UI component that displays when a provider's account
 * has been blocked by the administrator.
 * This component shows the verification message and block reason.
 */
const ProviderBlockedPage: React.FC<ProviderBlockedPageProps> = ({
  providerName,
  blockReason,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { logout } = useAuth()

  const handleContactSupport = () => {
    navigate('/messages')
  }

  const handleLogout = () => {
    logout()
    navigate('/provider-login')
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 750,
              width: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
              boxShadow: `0 12px 40px ${alpha(theme.palette.error.main, 0.15)}`,
            }}
          >
            {/* Header Section with Icon */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                p: 5,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 250,
                  height: 250,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.08),
                  animation: 'pulse 3s ease-in-out infinite',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -40,
                  left: -40,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.08),
                  animation: 'pulse 3s ease-in-out infinite 1.5s',
                },
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    opacity: 0.8,
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    opacity: 0.4,
                  },
                },
              }}
            >
              <Grow in timeout={1000}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 3,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.common.white, 0.95),
                    mb: 2,
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.error.dark, 0.3)}`,
                  }}
                >
                  <BlockOutlined
                    sx={{
                      fontSize: 64,
                      color: theme.palette.error.main,
                    }}
                  />
                </Box>
              </Grow>

              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 1.5,
                  position: 'relative',
                  zIndex: 1,
                  textShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                Account Access Restricted
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.95),
                  fontSize: '1.1rem',
                  position: 'relative',
                  zIndex: 1,
                  fontWeight: 500,
                }}
              >
                Your account has been temporarily suspended
              </Typography>
            </Box>

            {/* Content Section */}
            <Box sx={{ p: 5 }}>
              {providerName && (
                <Fade in timeout={1200}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'text.primary',
                      fontWeight: 500,
                    }}
                  >
                    Hello {providerName},
                  </Typography>
                </Fade>
              )}

              <Fade in timeout={1400}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    fontSize: '1rem',
                  }}
                >
                  We regret to inform you that your account access has been
                  restricted by our administrative team. This action has been
                  taken to ensure compliance with our platform standards and
                  regulatory requirements.
                </Typography>
              </Fade>

              {/* Status Badge */}
              {/* {verificationStatus && (
                <Fade in timeout={1600}>
                  <Box
                    sx={{
                      mb: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'text.secondary', fontWeight: 600 }}
                    >
                      Current Status:
                    </Typography>
                    <Chip
                      label={verificationStatus}
                      color="error"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        px: 1,
                      }}
                    />
                  </Box>
                </Fade>
              )} */}

              <Divider sx={{ my: 3 }} />

              {/* Verification Message Card */}
              {/* {verificationMessage && (
                <Grow in timeout={1800}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: alpha(theme.palette.error.main, 0.06),
                      border: `2px solid ${alpha(theme.palette.error.main, 0.25)}`,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <WarningAmber
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: 28,
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: theme.palette.error.dark,
                          fontSize: '1rem',
                        }}
                      >
                        Verification Issue
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      >
                        {verificationMessage}
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>
              )} */}

              {/* Block Reason Card */}
              {blockReason && (
                <Grow in timeout={2000}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                      border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        color: theme.palette.warning.dark,
                        fontSize: 28,
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: theme.palette.warning.dark,
                          fontSize: '1rem',
                        }}
                      >
                        Reason for Restriction
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      >
                        {blockReason}
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>
              )}

              {/* Information Card */}
              <Grow in timeout={2200}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                    borderRadius: 2,
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  <EmailOutlined
                    sx={{
                      color: theme.palette.info.main,
                      fontSize: 26,
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        color: theme.palette.info.dark,
                      }}
                    >
                      What should you do?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                      }}
                    >
                      Please contact our support team immediately to resolve
                      this issue. Our team will guide you through the necessary
                      steps to restore your account access. You may need to
                      provide updated documentation or address specific
                      compliance requirements.
                    </Typography>
                  </Box>
                </Paper>
              </Grow>

              {/* Footer Note */}
              <Fade in timeout={2600}>
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      textAlign: 'center',
                      lineHeight: 1.6,
                    }}
                  >
                    If you believe this restriction was made in error or have
                    questions about your account status, please reach out to our
                    support team. We're here to help resolve any issues as
                    quickly as possible.
                  </Typography>
                </Box>
              </Fade>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </Container>
  )
}

export default ProviderBlockedPage
